import { z } from 'zod'

import { DealDeliverableMix, DealDeliverableMixItem } from './deal'
import {
  DeliverableBillingType,
  DeliverableBillingTypeSchema,
} from './deliverable'

/**
 * Won-deal conversion (deals audit, 2026-07-06): turn a WON deal into a DRAFT
 * Contract plus Deliverable rows expanded from the deal's work mix, optionally
 * linked to a project. Request/response contract for POST /deals/:id/convert.
 *
 * The Zod parse happens at the service boundary (the deal controller has no
 * Zod pipe — same precedent as normalizeDealDeliverableMix).
 */

export const ConvertDealContractInputSchema = z.object({
  title: z.string().trim().min(1).max(255),
  contractNumber: z.string().trim().max(50).optional(),
  /** Org-configurable vocabulary (contractSettings.contractTypes). Required on Contract. */
  contractType: z.string().trim().min(1).max(50),
  startDate: z.coerce.date(),
  endDate: z.coerce.date().nullable().optional(),
  totalValue: z.union([z.number(), z.string()]).nullable().optional(),
  currency: z.string().trim().length(3).default('USD'),
})

export type ConvertDealContractInput = z.infer<
  typeof ConvertDealContractInputSchema
>

export const ConvertDealDeliverableInputSchema = z.object({
  name: z.string().trim().min(1).max(255),
  billingType: DeliverableBillingTypeSchema,
  /**
   * Deliberately optional with no default: mix sizes are t-shirt bands, not
   * value weights, so conversion never pro-rates deal.amount across rows.
   * The contract's totalValue carries the commercial figure.
   */
  allocatedValue: z.union([z.number(), z.string()]).nullable().optional(),
})

export type ConvertDealDeliverableInput = z.infer<
  typeof ConvertDealDeliverableInputSchema
>

export const ConvertDealRequestSchema = z.object({
  contract: ConvertDealContractInputSchema,
  deliverables: z.array(ConvertDealDeliverableInputSchema).max(100).default([]),
  /** Optional project to link: sets contract + deliverable projectId and repoints the deal's unassigned demands. */
  projectId: z.string().uuid().nullable().optional(),
})

export type ConvertDealRequest = z.infer<typeof ConvertDealRequestSchema>

export interface ConvertDealResult {
  contractId: string
  contractTitle: string
  deliverableIds: string[]
  projectId: string | null
  /** Demands whose projectId was set by this conversion (dealId kept for audit). */
  repointedDemandCount: number
}

/** Display labels for the deliverable billing-type vocabulary. */
export const DELIVERABLE_BILLING_TYPE_LABELS: Record<
  DeliverableBillingType,
  string
> = {
  TIME_AND_MATERIALS: 'Time & materials',
  FIXED_FEE: 'Fixed fee',
  BLENDED: 'Blended',
  RETAINER: 'Retainer',
  SUBSCRIPTION: 'Subscription',
}

/**
 * Expand a deal's rough work mix into editable deliverable prefill rows for the
 * conversion dialog. Pure: no defaults from org settings, no value pro-rating
 * (allocatedValue is always null). A row with quantity > 1 becomes numbered
 * copies ("{base} 1" ... "{base} n") so each lands as its own Deliverable.
 */
export function expandDealMixToDeliverables(
  mix: DealDeliverableMix | null | undefined,
): ConvertDealDeliverableInput[] {
  if (!mix?.length) return []
  return mix.flatMap((item: DealDeliverableMixItem) => {
    const base =
      item.label?.trim() || DELIVERABLE_BILLING_TYPE_LABELS[item.billingType]
    const quantity = Math.max(1, item.quantity ?? 1)
    return Array.from({ length: quantity }, (_, i) => ({
      name: quantity > 1 ? `${base} ${i + 1}` : base,
      billingType: item.billingType,
      allocatedValue: null,
    }))
  })
}
